import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useOptionalTaskRuntime } from "../context/TaskRuntimeContext";
import useEngineMediaSource from "../hooks/useEngineMediaSource";
import {
  DIRECT_GRBAS_CONSUMER_CONTEXT,
  issuePublishedAssignmentMediaDescriptor,
} from "../services/engineProtectedMedia";

export const PUBLISHED_ASSIGNMENT_PARTICIPANT_CONTEXT =
  "PUBLISHED_ASSIGNMENT_PARTICIPANT_EXECUTION";

export default function PresentedStimulusAudio({ trial, sampleLink = null, presentationKnown = false }) {
  const runtime = useOptionalTaskRuntime() || {};
  const stimuli = trial?.stimuli || [];
  const stimulus = stimuli.find(item => item.role === "primary")
    || stimuli.find(item => item.slotKey === "stimulus")
    || (stimuli.length === 1 ? stimuli.find(Boolean) : null)
    || null;
  const protectedMediaPilot = stimulus?.protectedMediaPilot === true;
  const [resolvedSampleLink, setResolvedSampleLink] = useState(sampleLink);
  const [presentationResolved, setPresentationResolved] = useState(presentationKnown || sampleLink != null);
  const participantContext = runtime.participantMediaContext?.consumerContext;
  const isPublishedAssignment = participantContext === PUBLISHED_ASSIGNMENT_PARTICIPANT_CONTEXT;
  const consumerContext = isPublishedAssignment
    ? PUBLISHED_ASSIGNMENT_PARTICIPANT_CONTEXT
    : DIRECT_GRBAS_CONSUMER_CONTEXT;
  const descriptorIssuerInput = useMemo(
    () => isPublishedAssignment ? { assignmentId: runtime.assignmentId } : {},
    [isPublishedAssignment, runtime.assignmentId]
  );

  useEffect(() => {
    let active = true;
    setResolvedSampleLink(sampleLink);
    setPresentationResolved(presentationKnown || sampleLink != null || protectedMediaPilot);
    if (!stimulus?.voiceSampleId && !protectedMediaPilot) setPresentationResolved(true);
    if (presentationKnown || sampleLink != null || protectedMediaPilot || !stimulus?.voiceSampleId) return () => { active = false; };
    axios.get(`${runtime.apiUrl}/voice-samples/${stimulus.voiceSampleId}`)
      .then(response => { if (active) { setResolvedSampleLink(response.data?.data?.sampleLink || null); setPresentationResolved(true); } })
      .catch(() => { if (active) { setResolvedSampleLink(null); setPresentationResolved(true); } });
    return () => { active = false; };
  }, [presentationKnown, protectedMediaPilot, runtime.apiUrl, sampleLink, stimulus?.voiceSampleId]);

  const media = useEngineMediaSource({
    apiUrl: runtime.apiUrl,
    token: runtime.token,
    sessionId: runtime.session?._id,
    trialId: trial?.trialId,
    stimulusInstanceId: stimulus?.stimulusInstanceId,
    sampleLink: resolvedSampleLink,
    consumerContext,
    protectedMediaPilot,
    descriptorIssuer: isPublishedAssignment
      ? issuePublishedAssignmentMediaDescriptor
      : undefined,
    descriptorIssuerInput,
  });

  if (!stimulus?.stimulusInstanceId) return <p role="alert">Presented media is unavailable.</p>;
  if (media.loading) return <p>Loading protected media...</p>;
  if (media.error) return <p role="alert">{media.error}</p>;
  if (!protectedMediaPilot && !presentationResolved) return <p>Loading presented media...</p>;
  return <audio controls preload="metadata" src={media.audioSrc} />;
}
